import React, { useEffect, useMemo, useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from '../shared/UI';
import Badge from '../shared/Badge';

const selectStyle = { width: '100%', padding: '7px 10px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' };
const sessionLabel = (s) => `${s.name}${s.time ? ` · ${s.time}` : ''}`;

// Each attending dancer takes one workshop in Session 1 and a different one
// in Session 2. Reached from the "Workshop Registration" timeline row. The
// school has a set number of spots in each workshop (scaled by its number of
// dancers) so every school's dancers spread out. Editable until that row's
// date passes — enforced server-side too (PUT /api/teacher/workshops).
export default function Workshops({ navigateTo }) {
  const { refetch } = useTeacherData();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [edits, setEdits] = useState({}); // dancer id -> { session1, session2 }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    sheetsClient.getWorkshops().then(setData).catch((err) => setLoadError(err.message));
  }, []);

  const value = (d, s) => edits[d.id]?.[`session${s}`] ?? d[`session${s}`];

  // Spots left for this school, counting unsaved changes on this page.
  const spotsLeft = useMemo(() => {
    const left = new Map();
    if (!data?.released) return left;
    const delta = new Map();
    const bump = (id, n) => id && delta.set(id, (delta.get(id) || 0) + n);
    data.dancers.forEach((d) => [1, 2].forEach((s) => {
      if (value(d, s) !== d[`session${s}`]) { bump(d[`session${s}`], -1); bump(value(d, s), 1); }
    }));
    data.runs.forEach((r) => left.set(r.runId, r.spotsLeft - (delta.get(r.runId) || 0)));
    return left;
    // value() reads edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, edits]);

  if (loadError) {
    return (
      <div style={{ padding: 20, maxWidth: 860, margin: '0 auto' }}>
        <Card><span style={{ fontSize: 13, color: '#C0392B' }}>{loadError}</span></Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 20, maxWidth: 860, margin: '0 auto' }}>
        <Skeleton width={220} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={420} height={14} style={{ marginBottom: 20 }} />
        <Card><Skeleton height={160} /></Card>
      </div>
    );
  }

  const header = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Button size="sm" onClick={() => navigateTo('dashboard')}>← Back to dashboard</Button>
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Workshops</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Every dancer takes one workshop in {data.sessions[1].name} and a different one in {data.sessions[2].name}.
        Your school has a set number of spots in each workshop, so dancers from every school get to mix.
        {data.deadline && <> Choose by <strong>{data.deadline}</strong>.</>}
      </p>
      {!data.hasSupervisor && (
        <div style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>Each school needs at least one teacher/chaperone to supervise workshops. Tick “Workshop supervisor” on the Teachers / Chaperones page.</span>
          <Button size="sm" onClick={() => navigateTo('staff')}>Go to Teachers / Chaperones</Button>
        </div>
      )}
    </>
  );

  if (!data.released) {
    return (
      <div style={{ padding: 20, maxWidth: 860, margin: '0 auto' }}>
        {header}
        <Card title="Coming soon">
          <p style={{ fontSize: 14, color: '#444', margin: 0 }}>
            The workshop schedule hasn’t been released yet. Once it is, you’ll see every workshop here and choose
            a {data.sessions[1].name} and {data.sessions[2].name} workshop for each of your dancers.
          </p>
        </Card>
      </div>
    );
  }

  const { runs, dancers, locked } = data;
  const runById = new Map(runs.map((r) => [r.runId, r]));
  const changeCount = Object.keys(edits).length;
  const complete = dancers.filter((d) => value(d, 1) && value(d, 2)).length;

  const setValue = (d, s, runId) => {
    const next = { session1: value(d, 1), session2: value(d, 2), [`session${s}`]: runId };
    setEdits((prev) => {
      const copy = { ...prev };
      if (next.session1 === d.session1 && next.session2 === d.session2) delete copy[d.id];
      else copy[d.id] = next;
      return copy;
    });
    setSavedAt(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      // The save sends back the updated page — no second request.
      const { view } = await sheetsClient.saveWorkshopAssignments(Object.entries(edits).map(([participantId, e]) => ({ participantId, ...e })));
      setData(view);
      setEdits({});
      setSavedAt(new Date());
      refetch.timeline();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 860, margin: '0 auto' }}>
      {header}

      {[1, 2].map((session) => (
        <Card key={session} title={sessionLabel(data.sessions[session])} collapsible defaultOpen={session === 1}>
          {runs.filter((r) => r.session === session).length === 0 && (
            <span style={{ fontSize: 13, color: '#999' }}>No workshops in this session.</span>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
            {runs.filter((r) => r.session === session).map((r) => (
              <WorkshopCard key={r.runId} run={r} left={spotsLeft.get(r.runId)} />
            ))}
          </div>
        </Card>
      ))}

      {data.supervisors?.length > 0 && (
        <Card title="Your workshop supervisors" subtitle="Assigned by OSSDF — each supervises one workshop in each session.">
          {data.supervisors.map((s) => (
            <div key={s.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '8px 0', borderBottom: '1px solid #F0F0F0', fontSize: 14 }}>
              <div style={{ flex: '1 1 160px', fontWeight: 500 }}>{s.firstName} {s.lastName}</div>
              {[1, 2].map((n) => {
                const r = runById.get(s[`session${n}`]);
                return (
                  <div key={n} style={{ flex: '1 1 220px', color: r ? '#444' : '#999' }}>
                    {data.sessions[n].name}: {r ? `${r.title} — ${r.spaceName}` : 'not assigned yet'}
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}

      <Card title="Your dancers" subtitle={`${complete} of ${dancers.length} dancer${dancers.length === 1 ? ' has' : 's have'} both workshops`}>
        {locked && (
          <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#666', marginBottom: 14 }}>
            The deadline has passed, so workshop choices can no longer be changed. Contact OSSDF if a dancer needs to switch.
          </div>
        )}
        {dancers.length === 0 && <span style={{ fontSize: 13, color: '#999' }}>No dancers on your roster yet.</span>}
        {dancers.map((d) => (
          <div key={d.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F0F0F0', background: edits[d.id] ? '#FFFBEA' : undefined }}>
            <div style={{ flex: '1 1 160px', fontSize: 14, fontWeight: 500 }}>
              {d.firstName} {d.lastName}
              {value(d, 1) && value(d, 2) && <span style={{ color: '#0F6E56', marginLeft: 6 }}>✓</span>}
            </div>
            {[1, 2].map((s) => {
              const current = value(d, s);
              const otherWorkshop = runById.get(value(d, s === 1 ? 2 : 1))?.workshopId;
              return (
                <div key={s} style={{ flex: '1 1 220px' }}>
                  <select style={{ ...selectStyle, background: locked ? '#F5F5F5' : '#fff' }} value={current} disabled={locked}
                    aria-label={`${data.sessions[s].name} workshop for ${d.firstName} ${d.lastName}`}
                    onChange={(e) => setValue(d, s, e.target.value)}>
                    <option value="">{data.sessions[s].name}: choose…</option>
                    {runs.filter((r) => r.session === s).map((r) => {
                      const left = spotsLeft.get(r.runId);
                      const isCurrent = r.runId === current;
                      const sameAsOther = r.workshopId === otherWorkshop;
                      return (
                        <option key={r.runId} value={r.runId} disabled={!isCurrent && (left <= 0 || sameAsOther)}>
                          {r.title}{sameAsOther ? ' (chosen for the other session)' : isCurrent ? '' : left <= 0 ? ' (full)' : ` (${left} left)`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            })}
          </div>
        ))}

        {saveError && <div style={{ fontSize: 13, color: '#C0392B', marginTop: 12 }}>{saveError}</div>}
        {!locked && dancers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
            <Button variant="primary" onClick={handleSave} disabled={!changeCount || saving}>
              {saving ? 'Saving…' : `✓ Save choices${changeCount ? ` (${changeCount})` : ''}`}
            </Button>
            {changeCount > 0 && !saving && <Button onClick={() => setEdits({})}>Undo changes</Button>}
            {savedAt && !saving && !changeCount && <span style={{ fontSize: 13, color: '#0F6E56' }}>Saved ✓</span>}
          </div>
        )}
        {!locked && (
          <p style={{ fontSize: 12, color: '#999', marginTop: 10, marginBottom: 0 }}>
            You can change choices any time{data.deadline ? ` until ${data.deadline}` : ''}. Spots go to whoever saves first.
          </p>
        )}
      </Card>
    </div>
  );
}

function WorkshopCard({ run, left }) {
  const [more, setMore] = useState(false);
  const long = (run.description || '').length > 160 || !!run.leaderBio;
  return (
    <div style={{ border: '1px solid #E5E5E5', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {run.leaderPhotoUrl
          ? <img src={run.leaderPhotoUrl} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#EEE', flexShrink: 0 }} />}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{run.title}</div>
          <div style={{ fontSize: 13, color: '#666' }}>{run.leaderName}{run.spaceName ? ` · ${run.spaceName}` : ''}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {run.style && <Badge variant="purple">{run.style}</Badge>}
        <Badge variant={left > 0 ? 'success' : left === 0 ? 'neutral' : 'danger'}>
          {left > 0 ? `${left} of ${run.spots} spots left` : left === 0 ? 'No spots left' : `${-left} over your spots`}
        </Badge>
      </div>
      {run.description && (
        <div style={{ fontSize: 13, color: '#444', whiteSpace: 'pre-line' }}>
          {more || run.description.length <= 160 ? run.description : `${run.description.slice(0, 160).trim()}…`}
        </div>
      )}
      {more && run.leaderBio && (
        <div style={{ fontSize: 13, color: '#444', whiteSpace: 'pre-line' }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>About {run.leaderName}</div>
          {run.leaderBio}
        </div>
      )}
      {long && (
        <button type="button" onClick={() => setMore(!more)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, color: '#ED1C24', fontSize: 13, cursor: 'pointer' }}>
          {more ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}
